// A chord string from a keyboard event, the chord's display form, its ProseMirror name, and
// where a key goes: the palette, the editor's plugins, or the document listener.

import type { Platform } from './chords.ts'

export interface KeyLike {
  key: string
  code: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
}

const NAMED: Record<string, string> = {
  Enter: 'Enter',
  Escape: 'Escape',
  Tab: 'Tab',
  Backspace: 'Backspace',
  Delete: 'Delete',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
  '.': 'Period',
  ',': 'Comma',
  ';': 'Semicolon',
  '\\': 'Backslash',
  ' ': 'Space',
}

/// The key's own name. Letters, digits, the period, the comma and the slash come from the
/// physical key, so Alt on a Mac (which turns J into a delta) and Shift (which turns 1 into an
/// exclamation mark and the period into >) still name the key the translator pressed.
function keyName(e: KeyLike): string | null {
  const letter = /^Key([A-Z])$/.exec(e.code)
  if (letter) return letter[1] ?? null
  const digit = /^Digit(\d)$/.exec(e.code)
  if (digit) return digit[1] ?? null
  if (e.code === 'Period' || e.code === 'Comma' || e.code === 'Slash') return e.code
  if (e.key in NAMED) return NAMED[e.key] ?? null
  if (/^F\d{1,2}$/.test(e.key)) return e.key
  if (e.key.length === 1) return e.key.toUpperCase()
  return null
}

const ALWAYS_CHORDS = new Set(['Enter', 'Escape', 'Tab', 'Backspace', 'Delete', 'Up', 'Down', 'Left', 'Right', 'Home', 'End', 'PageUp', 'PageDown'])

/// The chord a key event spells on the platform, or null when it is typing: a character
/// with no modifier, or with Shift alone.
export function chordOf(e: KeyLike, platform: Platform): string | null {
  const name = keyName(e)
  if (name === null) return null
  const mods: string[] = []
  if (platform === 'mac') {
    if (e.metaKey) mods.push('Mod')
    if (e.ctrlKey) mods.push('Ctrl')
  } else {
    if (e.ctrlKey) mods.push('Mod')
    if (e.metaKey) mods.push('Meta')
  }
  if (e.altKey) mods.push('Alt')
  if (e.shiftKey) mods.push('Shift')
  const modified = mods.some(m => m !== 'Shift')
  if (!modified && !ALWAYS_CHORDS.has(name) && !/^F\d/.test(name)) return null
  return [...mods, name].join('+')
}

const DISPLAY: Record<Platform, Record<string, string>> = {
  mac: { Mod: 'Cmd', Ctrl: 'Ctrl', Alt: 'Option', Shift: 'Shift', Meta: 'Cmd' },
  windows: { Mod: 'Ctrl', Ctrl: 'Ctrl', Alt: 'Alt', Shift: 'Shift', Meta: 'Win' },
}

const DISPLAY_KEYS: Record<string, string> = { Period: '.', Comma: ',', Slash: '/', Semicolon: ';', Backslash: '\\', Escape: 'Esc' }

/// `Mod+Shift+K` as `Cmd+Shift+K` on a Mac and `Ctrl+Shift+K` on Windows.
export function displayChord(chord: string, platform: Platform): string {
  const parts = chord.split('+')
  const key = parts.pop() ?? ''
  return [...parts.map(m => DISPLAY[platform][m] ?? m), DISPLAY_KEYS[key] ?? key].join('+')
}

const PM_KEYS: Record<string, string> = { Up: 'ArrowUp', Down: 'ArrowDown', Left: 'ArrowLeft', Right: 'ArrowRight', Period: '.', Comma: ',', Slash: '/', Semicolon: ';', Backslash: '\\', Space: ' ' }

/// The same chord in prosemirror-keymap's spelling, `Mod-Shift-Enter`, `Mod-k`.
export function proseMirrorKey(chord: string): string {
  const parts = chord.split('+')
  const key = parts.pop() ?? ''
  const name = PM_KEYS[key] ?? (key.length === 1 ? key.toLowerCase() : key)
  return [...parts, name].join('-')
}

export type KeyRoute = 'palette' | 'menu' | 'editor' | 'document' | 'none'

export interface KeyContext {
  paletteOpen: boolean
  /// The event's target is the menu bar or one of its menus.
  inMenu: boolean
  /// The event's target is inside the live editor.
  inEditor: boolean
  /// The event's target is a text field or a button outside the editor.
  inField: boolean
}

/// Where a chord goes. The palette owns every key while it is open. The bar and its menus keep
/// their unmodified keys, the arrows, Enter and Escape, and pass a modified chord on, so it runs
/// once whether a menu is open or not. Inside the editor the shell's ProseMirror keymap runs ahead
/// of the editor's own, so the document listener stays out. A field keeps its unmodified keys,
/// so Enter in a note box does not confirm a Segment.
export function routeKey(context: KeyContext, chord: string): KeyRoute {
  if (context.paletteOpen) return 'palette'
  if (context.inMenu && !/\+/.test(chord)) return 'menu'
  if (context.inEditor) return 'editor'
  if (context.inField && !/\+/.test(chord)) return 'none'
  return 'document'
}
